<template>
  <div class="drawer-filter product-filter-drawer">
    <a class="filter-trigger" :title="$t('products.filter.title')" @click="isOpen = !isOpen">
      <i class="fa fa-filter fa-2x"></i>
      <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
    </a>
    <div v-if="isOpen" class="filter-overlay" @click="isOpen = false"></div>
    <div class="filter-panel" :class="{ open: isOpen }">
      <div class="filter-panel-header">
        <span>{{ $t("products.filter.title") }}<template v-if="activeFilterCount > 0"> ({{ activeFilterCount }})</template></span>
        <md-button class="md-simple md-just-icon md-round" @click="isOpen = false"><md-icon>clear</md-icon></md-button>
      </div>
      <div class="filter-panel-body">
        <div class="filter-section">
          <div class="filter-section-title">{{ $t("products.filter.sectionProduct") }}</div>
          <md-field md-clearable>
            <label for="pf-bulky">{{ $t("products.bulky") }}</label>
            <md-select v-model="filter.bulky" id="pf-bulky" name="pf-bulky">
              <md-option value="true">{{ $t("products.filter.isBulky") }}</md-option>
              <md-option value="false">{{ $t("products.filter.isNotBulky") }}</md-option>
            </md-select>
          </md-field>
          <md-field md-clearable>
            <label for="pf-eol">{{ $t("products.filter.eol") }}</label>
            <md-select v-model="filter.eol" id="pf-eol" name="pf-eol">
              <md-option value="true">{{ $t("products.filter.isEol") }}</md-option>
              <md-option value="false">{{ $t("products.filter.isNotEol") }}</md-option>
            </md-select>
          </md-field>
          <div class="filter-select">
            <label class="filter-select-label">{{ $t("products.supplier") }}</label>
            <SelectSupplier v-model="filter.supplierId" :placeholder="$t('products.filterSupplier')" custom-class="custom-select" />
          </div>
          <div class="filter-select">
            <label class="filter-select-label">{{ $t("products.cabQuantity") }}</label>
            <v-select v-model="filter.cabQuantity" :options="options.cabQuantities" :placeholder="$t('products.filter.choose')" append-to-body :calculate-position="alignDropdown" class="custom-select" />
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">{{ $t("products.filter.sectionCustoms") }}</div>
          <div class="filter-row">
            <div class="filter-select">
              <label class="filter-select-label">{{ $t("products.hsUK") }}</label>
              <v-select v-model="filter.hsUK" :options="options.hsUK" :placeholder="$t('products.filter.choose')" append-to-body :calculate-position="alignDropdown" class="custom-select" />
            </div>
            <div class="filter-select">
              <label class="filter-select-label">{{ $t("products.hsEU") }}</label>
              <v-select v-model="filter.hsEU" :options="options.hsEU" :placeholder="$t('products.filter.choose')" append-to-body :calculate-position="alignDropdown" class="custom-select" />
            </div>
          </div>
          <div class="filter-row">
            <div class="filter-select">
              <label class="filter-select-label">{{ $t("products.hsCH") }}</label>
              <v-select v-model="filter.hsCH" :options="options.hsCH" :placeholder="$t('products.filter.choose')" append-to-body :calculate-position="alignDropdown" class="custom-select" />
            </div>
            <div class="filter-select">
              <label class="filter-select-label">{{ $t("products.customsTariff") }}</label>
              <v-select v-model="filter.customsTariff" :options="customsTariffOptions" :reduce="(o) => o.value" label="label" :placeholder="$t('products.filter.choose')" append-to-body :calculate-position="alignDropdown" class="custom-select" />
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">{{ $t("products.filter.sectionCarrier") }}</div>
          <div class="filter-row">
            <div v-for="c in carrierFields" :key="c.key" class="filter-select">
              <label class="filter-select-label">{{ c.label }}</label>
              <SelectCarrier v-model="filter[c.key]" :allowedCarriers="allowedCarriers" :placeholder="c.label" custom-class="custom-select" :calculate-position="alignDropdown" />
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">{{ $t("products.filter.sectionKpi") }}</div>
          <div v-for="k in kpiFields" :key="k.key" class="filter-kpi">
            <span class="filter-kpi-label">{{ $t("productCalculation.figure." + k.label) }}</span>
            <select v-model="kpi[k.key].op" class="filter-kpi-op">
              <option value="">{{ $t("products.filter.opNone") }}</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
              <option value="eq">=</option>
            </select>
            <input
              v-model="kpi[k.key].value"
              type="number"
              class="filter-kpi-value"
              :step="k.decimals ? 1 / Math.pow(10, k.decimals) : 1"
              :disabled="!kpi[k.key].op"
              :class="{ 'filter-kpi-value--invalid': !kpiValid(k) }"
            />
            <span class="filter-kpi-unit">{{ $t("products.filter.unit." + k.unit) }}</span>
          </div>
        </div>
      </div>
      <div class="filter-panel-footer">
        <md-button class="md-secondary" @click="clearFilters">{{ $t("shippingPlan.clear") }}</md-button>
        <md-button class="md-success" :disabled="!allKpiValid" @click="applyFilters">{{ $t("shippingPlan.apply") }}</md-button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosFactory from "@/services/axios.factory";
import { alignDropdown } from "@/mixins/dropdownPosition";
import { DrawerScrollLockMixin } from "@/mixins/drawerScrollLock";
import SelectCarrier from "@/components/SelectCarrier/SelectCarrier.vue";
import SelectSupplier from "@/components/SelectSupplier/SelectSupplier.vue";

// Schluessel wie ProductKpiFields in der API; decimals = erlaubte Nachkommastellen im Eingabefeld
const KPI_FIELDS = [
  // Bestand (Trello 3b9GL2pS, Update Q): Lager = Summe der Lagerplaetze, Zulauf aus den offenen Lieferplaenen
  { key: "inStock", label: "inStock", decimals: 0, unit: "pcs" },
  { key: "inFlow", label: "inFlow", decimals: 0, unit: "pcs" },
  { key: "totalStock", label: "totalStock", decimals: 0, unit: "pcs" },
  { key: "sold12", label: "sold12", decimals: 0, unit: "pcs" },
  { key: "sold3", label: "sold3", decimals: 0, unit: "pcs" },
  { key: "coverage12", label: "coverage12", decimals: 2, unit: "months" },
  { key: "coverage3", label: "coverage3", decimals: 2, unit: "months" },
  { key: "returnRate", label: "returnRate", decimals: 1, unit: "percent" },
  { key: "returns3", label: "returns3", decimals: 0, unit: "pcs" },
  { key: "storagePerMonth", label: "storagePerPiece", decimals: 2, unit: "eur" },
  { key: "averageStock", label: "averageStock", decimals: 2, unit: "pcs" },
  { key: "storageMonths", label: "storageMonths", decimals: 1, unit: "months" },
  { key: "storageCost", label: "storageCost", decimals: 2, unit: "eur" },
  { key: "turnover", label: "turnover", decimals: 1, unit: "none" },
];

const emptyFilter = () => ({
  bulky: null,
  eol: null,
  supplierId: null,
  cabQuantity: null,
  hsUK: null,
  hsEU: null,
  hsCH: null,
  customsTariff: null,
  carrierDe: null,
  carrierUk: null,
  carrierEu: null,
});

const emptyKpi = () => Object.fromEntries(KPI_FIELDS.map((k) => [k.key, { op: "", value: "" }]));

export default {
  name: "ProductFilterDrawer",
  components: { SelectCarrier, SelectSupplier },
  mixins: [DrawerScrollLockMixin],
  data() {
    return {
      isOpen: false,
      filter: emptyFilter(),
      kpi: emptyKpi(),
      options: { hsUK: [], hsEU: [], hsCH: [], customsTariffs: [], cabQuantities: [] },
      kpiFields: KPI_FIELDS,
      allowedCarriers: ["DHL", "DPD", "Spedition"],
    };
  },
  computed: {
    carrierFields() {
      return [
        { key: "carrierDe", label: this.$t("products.filter.carrierDe") },
        { key: "carrierUk", label: this.$t("products.filter.carrierUk") },
        { key: "carrierEu", label: this.$t("products.filter.carrierEu") },
      ];
    },
    customsTariffOptions() {
      return this.options.customsTariffs.map((v) => ({ value: v, label: `${this.$options.filters.formatNumberGerman(v, 1)} %` }));
    },
    activeKpis() {
      return KPI_FIELDS.filter((k) => this.kpi[k.key].op && this.kpi[k.key].value !== "" && this.kpi[k.key].value != null);
    },
    activeFilterCount() {
      return Object.values(this.filter).filter((v) => v !== null && v !== "").length + this.activeKpis.length;
    },
    allKpiValid() {
      return KPI_FIELDS.every((k) => this.kpiValid(k));
    },
  },
  created() {
    axiosFactory()
      .get("/shipping_data/products/filter_options")
      .then((res) => {
        this.options = { ...this.options, ...((res.data && res.data.data) || {}) };
      })
      .catch(() => {});
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    alignDropdown,
    // Ganzzahl bzw. hoechstens so viele Nachkommastellen wie das Feld erlaubt
    kpiValid(k) {
      const entry = this.kpi[k.key];
      if (!entry.op || entry.value === "" || entry.value == null) return true;
      const text = String(entry.value);
      if (!Number.isFinite(Number(text))) return false;
      const decimals = text.includes(".") ? text.split(".")[1].length : 0;
      return decimals <= k.decimals;
    },
    clearFilters() {
      this.filter = emptyFilter();
      this.kpi = emptyKpi();
    },
    applyFilters() {
      const kpiFilters = this.activeKpis.map((k) => `${k.key}:${this.kpi[k.key].op}:${Number(this.kpi[k.key].value)}`).join(",");
      this.$emit("apply-filters", {
        bulky: this.filter.bulky || "all",
        eol: this.filter.eol,
        supplierId: this.filter.supplierId,
        cabQuantity: this.filter.cabQuantity,
        hsUK: this.filter.hsUK,
        hsEU: this.filter.hsEU,
        hsCH: this.filter.hsCH,
        customsTariff: this.filter.customsTariff,
        carrierDe: this.filter.carrierDe,
        carrierUk: this.filter.carrierUk,
        carrierEu: this.filter.carrierEu,
        kpiFilters: kpiFilters || null,
      });
      this.isOpen = false;
    },
    onClickOutside(e) {
      // Auswahllisten (md-select, v-select mit append-to-body) liegen ausserhalb des Panels
      const inMenu = e.target.closest && e.target.closest(".md-menu-content, .vs__dropdown-menu");
      if (this.isOpen && !inMenu && document.body.contains(e.target) && !this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style>
.product-filter-drawer .filter-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  position: relative;
}
.product-filter-drawer .filter-trigger i.fa {
  font-size: 20px;
  color: #fff !important;
}
.product-filter-drawer .filter-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #f44336;
  color: #fff;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
}
.product-filter-drawer .filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1032;
}
.product-filter-drawer .filter-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 460px;
  max-width: 95vw;
  background: #fff;
  z-index: 1033;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 18px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  transition: transform 0.25s ease;
  visibility: hidden;
}
.product-filter-drawer .filter-panel.open {
  transform: translateX(0);
  visibility: visible;
}
.product-filter-drawer .filter-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px 10px 20px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  font-weight: 500;
}
.product-filter-drawer .filter-panel-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 20px 16px 20px;
  text-align: left;
}
.product-filter-drawer .filter-panel-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}
.product-filter-drawer .filter-section {
  margin-top: 14px;
}
.product-filter-drawer .filter-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
  margin-bottom: 4px;
}
.product-filter-drawer .filter-row {
  display: flex;
  gap: 14px;
}
.product-filter-drawer .filter-row > * {
  flex: 1;
  min-width: 0;
}
.product-filter-drawer .md-field {
  margin: 2px 0 14px 0;
  min-height: 42px;
}
.product-filter-drawer .filter-select {
  margin: 2px 0 12px 0;
}
.product-filter-drawer .filter-select-label {
  display: block;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 2px;
}
.product-filter-drawer .filter-kpi {
  display: grid;
  grid-template-columns: 1fr 64px 96px 52px;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 13px;
}
.product-filter-drawer .filter-kpi-op,
.product-filter-drawer .filter-kpi-value {
  padding: 3px 4px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 13px;
}
.product-filter-drawer .filter-kpi-value {
  text-align: right;
}
.product-filter-drawer .filter-kpi-value:disabled {
  background: #f6f6f6;
}
.product-filter-drawer .filter-kpi-value--invalid {
  border-color: #f44336;
}
.product-filter-drawer .filter-kpi-unit {
  color: #777;
  font-size: 12px;
}
</style>
